import { useEffect, useMemo } from 'react';
import ChartEmptyMessage from 'src/components/Charts/ChartEmptyMessage';
import { bulkColors } from 'src/constants/styles';
import ReBarChart from 'switch-fe-shared/components/Charts/ReBarChart';
import { BarSeries } from 'switch-fe-shared/components/Charts/recharts/types';
import { formatNumber, graphFileName } from 'switch-fe-shared/utils/formatting';
import {
  UtilityTypeOrAll,
  convertToAndFromBase,
} from 'switch-shared/modules/crud/utility/utility.enums';
import { SAST_TIMEZONE } from 'switch-shared/utils/timezone';

import { BucketOptions, DateRange } from '../../../context/dashboardContext';
import { useApplicationGroups } from '../../../hooks/useApplicationGroups';
import { useApplications } from '../../../hooks/useApplications';
import useEffectiveApplicationGroupId from '../../../hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from '../../../hooks/useEffectiveApplicationId';
import { useAggregateP1SplitByZoneQuery } from '../../../services/switch-rest-api/utility-data.service';

import '../../../styles/chart/LoadingChart.css';
import { getUtilityUnits } from '../../../utils/formatters';
import {
  isAllSelection,
  toConcreteUtilityType,
} from '../../../utils/selection';
import { Metrics } from '../../dashboard/components/widgets/UtilityUsageWidget';

interface Props {
  handleMetrics: (metrics: Metrics, priorMetrics?: Metrics) => void;
  dateRange: DateRange;
  bucketOptions: BucketOptions;
  utility: UtilityTypeOrAll;
  onLoadingChange?: (isLoading: boolean) => void;
}

const ZoneUsageGraph = ({
  handleMetrics,
  dateRange,
  bucketOptions,
  utility,
  onLoadingChange,
}: Props) => {
  /* Hooks & Use Effects */

  const id = graphFileName('zone_', dateRange.start, dateRange.end);

  const appGroupId = useEffectiveApplicationGroupId();
  const { applicationGroups: appGroups } = useApplicationGroups();
  const appId = useEffectiveApplicationId();
  const { applications: apps } = useApplications();

  /* States */

  /* API Calls */

  const { data, isLoading, isFetching } = useAggregateP1SplitByZoneQuery(
    {
      ...(isAllSelection(appId)
        ? {
            applicationGroupId: appGroupId,
          }
        : {
            applicationId: appId,
          }),
      startTimeISO: dateRange.start.toISOString(),
      endTimeISO: dateRange.end.toISOString(),
      bucketSize: bucketOptions.size,
      bucketUnit: bucketOptions.unit,
      utilityType: toConcreteUtilityType(utility),
      timezone: SAST_TIMEZONE,
      includePriorPeriod: true,
    },
    { skip: appGroupId == null || appId == null }
  );

  const isGraphLoading = isLoading;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isGraphLoading);
      return () => {
        onLoadingChange(false);
      };
    }
    return undefined;
  }, [isGraphLoading, onLoadingChange]);

  /* Functions & Functional Components */

  const getSelectedAppOrGroupName = () => {
    return isAllSelection(appId)
      ? (appGroups.find((ag) => ag._id === appGroupId)?.name ??
          'Unknown application group')
      : (apps.find((a) => a._id === appId)?.name ?? 'Unknown application');
  };

  const series = useMemo<BarSeries[]>(() => {
    const buckets = data?.buckets ?? [];

    const convertValue = (value?: number) => {
      return value
        ? convertToAndFromBase(value, toConcreteUtilityType(utility), true)
        : 0;
    };

    return [
      {
        name: 'Bulk Usage',
        data: buckets.map((b) => convertValue(b.deltaP1Bulk)),
        group: 'bulk',
      },
      {
        name: 'Sub-Meter Usage',
        data: buckets.map((b) => convertValue(b.deltaP1SubMeter)),
        group: 'tenant',
      },
      {
        name: 'Unmetered Usage',
        data: buckets.map((b) => {
          const bulk = b.deltaP1Bulk ?? 0;
          const tenant = b.deltaP1SubMeter ?? 0;
          return Math.max(0, convertValue(bulk - tenant));
        }),
        group: 'tenant',
      },
    ];
  }, [data, utility]);

  const categories: string[] = useMemo(
    () => (data?.buckets ? data.buckets.map((b) => b.name) : []),
    [data]
  );

  const unit = useMemo(() => {
    return getUtilityUnits(utility, true);
  }, [utility]);

  const tooltipTotals = useMemo(
    () => [
      { label: 'Total Bulk', prefixes: ['Bulk'] },
      { label: 'Total Sub-Meter', prefixes: ['Sub-Meter'] },
    ],
    []
  );

  useEffect(() => {
    const totalBulk = data?.buckets.reduce(
      (acc, cur) => acc + cur.deltaP1Bulk,
      0
    );
    const totalSub = data?.buckets.reduce(
      (acc, cur) => acc + cur.deltaP1SubMeter,
      0
    );

    const totalCommunal = (totalBulk ?? 0) - (totalSub ?? 0);

    // Calculate prior period totals if available
    const priorBulk = data?.priorBuckets?.reduce(
      (acc, cur) => acc + cur.deltaP1Bulk,
      0
    );
    const priorSub = data?.priorBuckets?.reduce(
      (acc, cur) => acc + cur.deltaP1SubMeter,
      0
    );
    const priorCommunal =
      priorBulk !== undefined && priorSub !== undefined
        ? (priorBulk ?? 0) - (priorSub ?? 0)
        : undefined;

    const currentMetrics = {
      bulk: totalBulk !== undefined ? totalBulk / 1000 : undefined,
      subMeter: totalSub !== undefined ? totalSub / 1000 : undefined,
      communal: totalCommunal !== undefined ? totalCommunal / 1000 : undefined,
    };

    const priorMetrics =
      priorBulk !== undefined || priorSub !== undefined
        ? {
            bulk: priorBulk !== undefined ? priorBulk / 1000 : undefined,
            subMeter: priorSub !== undefined ? priorSub / 1000 : undefined,
            communal:
              priorCommunal !== undefined ? priorCommunal / 1000 : undefined,
          }
        : undefined;

    handleMetrics({
      bulk:
        totalBulk !== undefined
          ? convertToAndFromBase(
              totalBulk,
              toConcreteUtilityType(utility),
              true
            )
          : undefined,
      subMeter:
        totalSub !== undefined
          ? convertToAndFromBase(totalSub, toConcreteUtilityType(utility), true)
          : undefined,
      communal:
        totalCommunal !== undefined
          ? convertToAndFromBase(
              totalCommunal,
              toConcreteUtilityType(utility),
              true
            )
          : undefined,
    });
  }, [data, handleMetrics]);

  return !isFetching && data?.buckets.length === 0 ? (
    <ChartEmptyMessage
      message={`${data.errMessage} for ${getSelectedAppOrGroupName()} - ${utility}`}
    />
  ) : (
    <div
      className={isFetching ? 'pulsing-chart' : undefined}
      style={{ height: '100%', paddingTop: 25 }}
    >
      <ReBarChart
        key={id}
        isLoading={isLoading}
        series={series}
        categories={categories}
        colors={[
          bulkColors.BULK_COLOR,
          bulkColors.TENANT_COLOR,
          bulkColors.COMMUNAL_COLOR,
        ]}
        yTitle={`Usage (${unit})`}
        unit={unit}
        stackGroups
        showLegend
        fileName={id}
        yFormatter={(val) => formatNumber(Math.abs(val))}
        tooltipTotals={tooltipTotals}
      />
    </div>
  );
};

export default ZoneUsageGraph;
